import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPath } from "../lib/url";

// Three ways into the portal, in order of preference:
//   1. Existing session cookie. We try /api/me first and skip the form
//      entirely if it returns 200.
//   2. ?k=<base64url(username:password)> in the URL. JP's admin issues
//      these one-click login links. We POST the decoded creds to
//      /api/login, strip the param from the URL on success so it
//      doesn't linger in browser history, then route to /batch.
//   3. Manual form fallback for cases where a freelancer just has
//      raw creds, not a link.

interface DecodedLink {
  username: string;
  password: string;
}

// base64url decoder — handles missing padding and URL-safe characters.
function decodeLink(token: string): DecodedLink | null {
  try {
    let normalized = token.replace(/-/g, "+").replace(/_/g, "/");
    while (normalized.length % 4 !== 0) normalized += "=";
    const raw = atob(normalized);
    const idx = raw.indexOf(":");
    if (idx <= 0 || idx === raw.length - 1) return null;
    return {
      username: raw.slice(0, idx),
      password: raw.slice(idx + 1),
    };
  } catch {
    return null;
  }
}

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function tryLoginWithToken(token: string): Promise<boolean> {
      const decoded = decodeLink(token);
      if (!decoded) return false;
      setLinkBusy(true);
      try {
        const res = await fetch(apiPath("/api/login"), {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(decoded),
        });
        if (!res.ok) {
          setError("That login link didn't work. Ask your editor for a fresh one.");
          setLinkBusy(false);
          return false;
        }
        // Scrub the credential out of the URL so it doesn't sit in
        // history / referrer headers any longer than necessary.
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState(null, "", cleanUrl);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setLinkBusy(false);
        return false;
      }
    }

    (async () => {
      // Magic-link login takes precedence over any existing session.
      // Without this, clicking a link for batch A while still signed
      // into batch B would silently bring the freelancer back to batch
      // B's articles (and they'd see the wrong videos).
      const params = new URLSearchParams(window.location.search);
      const token = params.get("k");
      if (token) {
        // Clear any existing session so the new token gets a clean slate
        // (the worker's set-cookie for the new batch overwrites this,
        // but logging out first guarantees no stale cookie survives if
        // the token login itself fails).
        try {
          await fetch(apiPath("/api/logout"), { method: "POST" });
        } catch {
          // ignore — best effort
        }
        const ok = await tryLoginWithToken(token);
        if (!cancelled && ok) {
          navigate("/batch", { replace: true });
        }
        return;
      }

      // No token: existing session cookie?
      try {
        const res = await fetch(apiPath("/api/me"));
        if (!cancelled && res.ok) {
          navigate("/batch", { replace: true });
          return;
        }
      } catch {
        // ignore — fall through to manual form
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(apiPath("/api/login"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!res.ok) {
        setError("Username or password didn't match. Try again.");
        setBusy(false);
        return;
      }
      navigate("/batch", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  if (linkBusy) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <div className="login-brand">
            <span className="mark" />
            Flywheel Studio
          </div>
          <h1 className="login-title">Signing you in…</h1>
          <p className="login-sub">One moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-brand">
          <span className="mark" />
          Flywheel Studio
        </div>
        <h1 className="login-title">Sign in to record</h1>
        <p className="login-sub">
          If your editor sent you a one-click link, you're already in — no
          need to type anything. If you only have a username and password,
          use the form below.
        </p>

        {error && <div className="errstate">{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="field">
            <label className="field-label" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              autoCapitalize="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="quiet-river-cards"
              required
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="btn primary block"
            disabled={busy || !username || !password}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
